import React, { useEffect, useState, FunctionComponent, ReactElement, SyntheticEvent } from 'react';

import { BFLoader } from '@components/common/loader/main';
import { CustomFieldValuesListResponse, FlattenedCustomFieldKeyValuesMap } from '@api/v4/assets/customFieldTypes';
import { useFetch } from '@api/ApiHelper';
import { flattenCustomFieldKeyValuesList } from '@api/v4/assets/helpers';
import { determineUGTLocale } from '@components/asset/modal/tabs/edit/helpers';
import { customFieldQuery, valuesAsString } from '@components/asset/modal/tabs/asset_details/helpers/custom-field-strings';
import { getResourceIsCollectionOrShareLink } from '@helpers/resource';
import { getCurrentUserIsAnonymousOrGuest } from '@helpers/user';

interface CustomFieldsAccordionProps {
  count: number;
  assetKey?: string;
  closeModal?: () => void;
  customFieldKeyValuesList?: FlattenedCustomFieldKeyValuesMap;
  loading?: boolean;
}

export const CustomFieldsAccordion: FunctionComponent<CustomFieldsAccordionProps> = (props) => {
  const [showMore, setShowMore] = useState(false);
  const [localCustomFieldKeyValuesList, setLocalCustomFieldKeyValuesList] = useState<FlattenedCustomFieldKeyValuesMap>({});
  const { assetKey, closeModal, count } = props;
  const showCustomFieldsSection = getResourceIsCollectionOrShareLink() && getCurrentUserIsAnonymousOrGuest(BFG.currentUser) ? BFG.showCustomFieldsOverviewTab : true;
  const url = `/api/v4/assets/${assetKey}/custom_field_values`;
  const customFieldValuesFetch = useFetch<CustomFieldValuesListResponse>({
    fetchOnMount: false,
    include: 'custom_field_key',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    params: { digest: BFG.manifest?.digest, ugt_locale: determineUGTLocale() },
    url,
  });

  const customFieldKeyValuesList = props.customFieldKeyValuesList || localCustomFieldKeyValuesList;
  const loading = props.loading || customFieldValuesFetch.loading;

  useEffect(() => {
    if (customFieldValuesFetch.response) {
      setLocalCustomFieldKeyValuesList(flattenCustomFieldKeyValuesList(customFieldValuesFetch.response));
    }
  }, [customFieldValuesFetch.response]);

  useEffect(() => {
    if (!props.customFieldKeyValuesList && showCustomFieldsSection && props.assetKey) {
      customFieldValuesFetch.fetch();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filterOnCustomFields = (e: SyntheticEvent, key: string, values: string[]): void => {
    e.preventDefault();
    e.stopPropagation();

    if (closeModal) {
      closeModal(); // method to close modal in org search context
    } else {
      BF.dialog.dismiss(); // method to close modal in all other contexts
    }

    const query = customFieldQuery(key, values);
    BF.fx.fillInAndFilterQuery(query);
  };

  const renderRow = ({ customFieldKey, customFieldValues }): ReactElement => (
    <tr
      key={customFieldKey.id}
      className={`s-custom-field cf-override-${BFG.currentUser.role}-${customFieldKey.name.replace(' ', '_')}`}
      onClick={!BFG.showPageLite ? (e): void => filterOnCustomFields(e, customFieldKey.name, customFieldValues.map(({ value }) => value)) : undefined}
    >
      <td className="col-xs-4">{customFieldKey.name}</td>
      <td className="col-xs-8">
        {valuesAsString(customFieldValues)}
      </td>
    </tr>
  );

  if (loading) {
    return <BFLoader />;
  }

  if (!Object.keys(customFieldKeyValuesList).length) {
    return null;
  }

  return (
    <>
      {showCustomFieldsSection && (
        <>
          <span className="s-asset-details-title modal-label">{bfTranslate('Custom Fields')}</span>
          <table className="table s-custom-fields-section">
            <tbody>
              {Object.keys(customFieldKeyValuesList).slice(0, showMore ? undefined : count).map((key) => (
                renderRow(customFieldKeyValuesList[key])
              ))}
              {Object.keys(customFieldKeyValuesList).length > count && (
                <tr
                  className="s-hidden-fields"
                  onClick={(): void => setShowMore((prevState) => !prevState)}
                >
                  <td colSpan={2}>
                    {showMore ? bfTranslate('Hide Some Fields') : bfTranslate('Show All Fields')}
                    <span
                      aria-hidden="true"
                      className={showMore ? 'bff-close' : 'bff-caret-down'}
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </>
      )}
    </>
  );
};
